import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Observable } from 'rxjs';
import { debounceTime, map, startWith } from 'rxjs/operators';
import ISO6391 from 'iso-639-1';
import { SelectedLocationsRadioButton } from '../bulk-action-popup/bulk-action-popup.component';
import { TranslateModule } from '@ngx-translate/core';
import { MatButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import { GxInputDirective } from '../../../../../directives/gxinput.directive';
import { MatChipGrid, MatChipInput, MatChipRemove, MatChipRow } from '@angular/material/chips';
import { MatFormField } from '@angular/material/form-field';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatIcon } from '@angular/material/icon';
import { PushPipe } from '@ngrx/component';

interface ExportPlaceNamesDialogData {
  areLocationsSelected: boolean;
  areLocationsFiltered: boolean;
}

export interface LanguageCode {
  name: string,
  code: string
}

@Component({
    selector: 'app-export-place-names-popup',
    templateUrl: './export-place-names-popup.component.html',
    styleUrls: ['./export-place-names-popup.component.scss'],
    standalone: true,
  imports: [
    MatDialogTitle,
    MatIcon,
    MatDialogContent,
    MatRadioGroup,
    FormsModule,
    MatRadioButton,
    MatFormField,
    MatChipGrid,
    MatAutocompleteTrigger,
    MatChipInput,
    GxInputDirective,
    ReactiveFormsModule,
    MatAutocomplete,
    MatOption,
    MatChipRemove,
    MatDialogActions,
    MatButton,
    MatDialogClose,
    TranslateModule,
    PushPipe,
    MatChipRow,
  ],
})
export class ExportPlaceNamesPopupComponent implements OnInit {

  // HTML
  selectedLocationsRadioButton = SelectedLocationsRadioButton;

  // location-subset for which to export placenames (radio-group)
  chosenLocations: SelectedLocationsRadioButton = SelectedLocationsRadioButton.ALL;

  // language in which to export placenames (chip-list)
  separatorKeysCodes: number[] = [ENTER, COMMA];
  languageCtrl: FormControl<string|null> = new FormControl('null');
  filteredLanguages: Observable<string[]>;
  selectedLanguages: LanguageCode[] = [];
  allLanguages: string[];

  @ViewChild('languageInput') languageInputRef: ElementRef<HTMLInputElement>;
  @ViewChild('auto') matAutocompleteRef: MatAutocomplete;

  constructor(public dialogRef: MatDialogRef<ExportPlaceNamesPopupComponent>,
              @Inject(MAT_DIALOG_DATA) public data: ExportPlaceNamesDialogData) {
  }

  ngOnInit() {
    this.chosenLocations = this.data.areLocationsSelected ?
        SelectedLocationsRadioButton.SELECTED :
        (this.data.areLocationsFiltered ?
            SelectedLocationsRadioButton.FILTERED : SelectedLocationsRadioButton.ALL);
    this.initLanguageNames();
    this.filteredLanguages = this.languageCtrl.valueChanges.pipe(
        startWith(null as string),
        debounceTime(500),
        map((language: string | null) => language ? this._filterLanguages(language) : this.allLanguages.slice()),
    );
  }

  removeLanguage(language: LanguageCode): void {
    const index = this.selectedLanguages.indexOf(language);

    if (index >= 0) {
      this.selectedLanguages.splice(index, 1);
    }
  }

  selectLanguage(event: MatAutocompleteSelectedEvent): void {
    this.selectedLanguages.push({
      name: event.option.value,
      code: this.getLanguageCode(event.option.value),
    });

    this.languageInputRef.nativeElement.value = null;
    this.languageCtrl.setValue(null);
  }


  private _filterLanguages(keyword: string): string[] {
    const filterValue = keyword.toLowerCase();

    return this.allLanguages.filter(lng => {
      return lng.toLowerCase().indexOf(filterValue) === 0;
    });
  }

  /**
   * Generates a result that gets returned to the parent component.
   */
  generateResult() {
    if (this.selectedLanguages.length === 0) {
      return null;
    }

    const selectedLanguagesCodes: string[] = this.selectedLanguages.map(language => language.code);

    return {
      locationSubset: this.chosenLocations,
      languageCodes: selectedLanguagesCodes,
    };
  }

  /**
   * Disable the EXPORT button if no fields are selected to be exported.
   */

  noLanguageSelected() {
    let disableExportBtn = true;

    if (this.selectedLanguages && !!this.selectedLanguages.length) {
      disableExportBtn = false;
    }

    return disableExportBtn;
  }


  /**
   * Get language code by language name.
   * @param languageName (e.g. "English")
   */
  getLanguageCode(languageName: string): string {

    let languageCode = ISO6391.getCode(languageName);
    return languageCode ? languageCode : null;

  }

  initLanguageNames() {
    this.allLanguages = ISO6391.getAllNames();
  }
}
